/**
 * The §8 gate, as it appears on the new-opportunity form.
 *
 * **One question, before any other field.** The wording is load-bearing and
 * must not be paraphrased, which is why every string here comes from
 * `@menlo/shared` rather than being retyped: it asks about *data entry*, not
 * about the program. "Is this opportunity ITAR?" would be answered *yes* for
 * most defense deals, refusing most of the funnel and pushing those deals into
 * spreadsheets — a worse compliance outcome and a lost forecast.
 *
 * Folding "or I'm not sure" into the blocking answer is the other half: without
 * it, the safe answer for an uncertain rep is to guess "No".
 *
 * There is **no default**. §8 item 1 calls a defaulted attestation a
 * click-through — except where §8 item 6 applies, where an
 * `export_sensitive` account or program pre-selects **Yes** so a rep must
 * actively override.
 */

import { ShieldAlert } from 'lucide-react';
import {
  ATTACHMENT_ATTESTATION_QUESTION,
  EXPORT_GATE_ANSWER_NO,
  EXPORT_GATE_ANSWER_YES,
  EXPORT_GATE_HEADING,
  EXPORT_GATE_HELP,
  EXPORT_GATE_QUESTION,
} from '@menlo/shared';
import { cn } from '@/lib/utils';

export interface ExportAttestationProps {
  /** `null` until answered — the question has no default (§8 item 1). */
  value: boolean | null;
  onChange: (value: boolean) => void;
  /**
   * §8 item 6: the answer defaults to "Yes" for an `export_sensitive` account
   * or program, so a rep must actively override rather than click through.
   * Set by a human on the account, never derived from segment.
   */
  preselectedYesReason?: string | undefined;
  disabled?: boolean;
}

export function ExportAttestation({
  value,
  onChange,
  preselectedYesReason,
  disabled = false,
}: ExportAttestationProps): React.ReactElement {
  return (
    <fieldset className="rounded-lg border bg-card p-4" disabled={disabled}>
      <legend className="flex items-center gap-2 px-1 text-sm font-semibold">
        <ShieldAlert className="h-4 w-4 text-amber-600" aria-hidden="true" />
        {EXPORT_GATE_HEADING}
      </legend>

      <p className="mt-2 text-sm">{EXPORT_GATE_QUESTION}</p>

      <div className="mt-3 space-y-2">
        <AnswerOption
          name="export-attestation"
          checked={value === false}
          onSelect={() => onChange(false)}
          label={EXPORT_GATE_ANSWER_NO}
        />
        <AnswerOption
          name="export-attestation"
          checked={value === true}
          onSelect={() => onChange(true)}
          label={EXPORT_GATE_ANSWER_YES}
        />
      </div>

      <p className="mt-3 text-xs italic text-muted-foreground">{EXPORT_GATE_HELP}</p>

      {preselectedYesReason !== undefined && (
        <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">
          {preselectedYesReason} — the answer is pre-selected as{' '}
          <strong>&ldquo;Yes, or I&rsquo;m not sure&rdquo;</strong>. Change it only if you are
          certain no controlled technical data will be entered here.
        </p>
      )}
    </fieldset>
  );
}

function AnswerOption({
  name,
  checked,
  onSelect,
  label,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  label: string;
}): React.ReactElement {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm',
        checked ? 'border-primary bg-accent' : 'border-input',
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        className="mt-0.5 h-4 w-4"
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * §8 item 7: **every upload carries its own attestation, on both paths.**
 * Uploads are the one path the pattern scan cannot see, and v1 supports files
 * up to 1 GB — which in this business means CAD assemblies, measurement
 * datasets and simulation output, precisely the category §8 refuses.
 *
 * It is a checkbox in the upload dialog, required before the presign call
 * **and before the multipart initiation** — not a banner to click past.
 */
export function AttachmentAttestation({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (value: boolean) => void;
}): React.ReactElement {
  return (
    <fieldset className="rounded-md border p-3">
      <legend className="px-1 text-xs font-medium">{EXPORT_GATE_HEADING}</legend>
      <p className="text-sm">{ATTACHMENT_ATTESTATION_QUESTION}</p>
      <div className="mt-2 space-y-2">
        <AnswerOption
          name="attachment-attestation"
          checked={value === false}
          onSelect={() => onChange(false)}
          label="No — commercial information only"
        />
        <AnswerOption
          name="attachment-attestation"
          checked={value === true}
          onSelect={() => onChange(true)}
          label="Yes, or I'm not sure"
        />
      </div>
      <p className="mt-2 text-xs italic text-muted-foreground">{EXPORT_GATE_HELP}</p>
    </fieldset>
  );
}

/**
 * What the user sees when the server refuses: **no record is created**, and
 * the text naming the approved system or process comes from `app_settings`
 * through the `403 EXPORT_BLOCKED` message — never hardcoded here, because an
 * Admin changes it without a deploy.
 *
 * There is no restricted record state, no banner, and no
 * disabled-with-explanation control anywhere in this product. The answer is
 * refusal.
 */
export function ExportRefusal({
  refusalText,
  onBack,
}: {
  refusalText: string;
  onBack?: () => void;
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-amber-600/40 bg-amber-50 p-5 text-amber-950">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="space-y-3 text-sm">
          <p className="font-semibold">No record has been created.</p>
          <p className="whitespace-pre-line">{refusalText}</p>
          <p className="text-xs">
            Your answer has been recorded with your name, the time, and the current policy version.
            Commercial deal information for controlled programs is fine — if no controlled technical
            data will be entered here, go back and answer again.
          </p>
          {onBack !== undefined && (
            <button
              type="button"
              onClick={onBack}
              className="text-xs font-medium underline underline-offset-2"
            >
              Back to the question
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
